import React, { useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus, Image as ImageIcon, Film, Columns, LayoutGrid, X, Type } from "lucide-react";
import DropZone from "./DropZone";

/**
 * Reorderable content block editor for Case bodies (and reusable elsewhere).
 * Each block is one of: image | video | dual | gallery. Blocks are sorted vertically
 * via drag handles; per-block spacing (px) is a slider. Data shape:
 *   { id, type: "image"|"video"|"dual"|"gallery", spacing_px: number, ...typeSpecific }
 *
 *   image   → { src, alt }
 *   video   → { src, poster, alt }
 *   dual    → { left: {src, alt, kind:"image"|"video", poster?}, right: {...} }
 *   gallery → { items: [{ src, alt, kind:"image"|"video"|"gif", poster? }] }
 */

let _bid = 0;
export const newBlockId = () => `blk_${Date.now()}_${++_bid}`;

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

function SortableBlock({ block, index, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const upd = (patch) => onChange({ ...block, ...patch });
  // Thumbnail shown IN THE HEADER — makes it easy to identify each block at a
  // glance while drag-and-dropping (previously only the block type icon was
  // visible, which is useless if you have 10 image blocks in the same case).
  const headerThumb = getHeaderThumb(block);

  return (
    <div ref={setNodeRef} style={style} className="border border-black/10 rounded-md bg-white overflow-hidden" data-testid={`block-${block.type}-${index}`}>
      <header className="flex items-center gap-2 px-3 py-2 bg-neutral-50 border-b border-black/10">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-black transition-colors" title="Arrastar para reordenar">
          <GripVertical className="w-4 h-4" />
        </button>
        <BlockIcon type={block.type} />
        {headerThumb ? (
          <div className="w-10 h-10 rounded overflow-hidden bg-neutral-200 border border-black/10 shrink-0" data-testid={`block-thumb-${index}`}>
            {headerThumb.kind === "video" ? (
              <video src={headerThumb.src} className="w-full h-full object-cover" muted preload="metadata" />
            ) : (
              <img src={headerThumb.src} alt="" className="w-full h-full object-cover" />
            )}
          </div>
        ) : (
          <div className="w-10 h-10 rounded bg-neutral-100 border border-dashed border-black/15 shrink-0 flex items-center justify-center" title="Sem imagem definida ainda">
            <ImageIcon className="w-4 h-4 text-neutral-300" />
          </div>
        )}
        <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{TYPE_LABEL[block.type] || block.type}</span>
        <span className="text-[10px] font-mono text-neutral-400">#{index + 1}</span>
        {block.type === "gallery" && (block.items?.length ?? 0) > 0 && (
          <span className="text-[10px] font-mono text-neutral-400 ml-1">· {block.items.length} itens</span>
        )}
        <div className="ml-auto flex items-center gap-3">
          <label className="text-[10px] text-neutral-500 flex items-center gap-1">
            Espaço abaixo:
            <input type="range" min={0} max={200} step={4} value={block.spacing_px ?? 24} onChange={(e) => upd({ spacing_px: Number(e.target.value) })} data-testid={`block-spacing-${index}`} className="w-24" />
            <span className="font-mono w-10">{block.spacing_px ?? 24}px</span>
          </label>
          <button onClick={() => onRemove(block.id)} className="text-red-500 hover:text-red-700 transition-colors" title="Remover"><Trash2 className="w-4 h-4" /></button>
        </div>
      </header>
      <div className="p-3">
        {block.type === "image" && <ImageBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "video" && <VideoBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "dual" && <DualBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "gallery" && <GalleryBlockEditor block={block} upd={upd} index={index} />}
        {block.type === "text" && <TextBlockEditor block={block} upd={upd} index={index} />}
      </div>
    </div>
  );
}

/** Extract the best thumbnail candidate for a block, to show in the header. */
function getHeaderThumb(block) {
  const isVideo = (u) => /\.(mp4|webm|mov)/i.test(u || "");
  if (block.type === "image" && block.src) return { src: resolveSrc(block.src), kind: isVideo(block.src) ? "video" : "image" };
  if (block.type === "video") {
    const src = block.poster || block.src;
    if (src) return { src: resolveSrc(src), kind: block.poster ? "image" : "video" };
  }
  if (block.type === "dual") {
    const left = block.left?.src, right = block.right?.src;
    if (left) return { src: resolveSrc(left), kind: block.left?.kind === "video" ? "video" : "image" };
    if (right) return { src: resolveSrc(right), kind: block.right?.kind === "video" ? "video" : "image" };
  }
  if (block.type === "gallery" && Array.isArray(block.items) && block.items.length > 0) {
    const first = block.items.find((i) => i.src);
    if (first) return { src: resolveSrc(first.src), kind: first.kind === "video" ? "video" : "image" };
  }
  if (block.type === "text" && (block.text || block.html)) {
    return null; // text blocks use the type icon
  }
  return null;
}

function BlockIcon({ type }) {
  const cls = "w-4 h-4 text-accent-orange";
  if (type === "image") return <ImageIcon className={cls} />;
  if (type === "video") return <Film className={cls} />;
  if (type === "dual") return <Columns className={cls} />;
  if (type === "gallery") return <LayoutGrid className={cls} />;
  if (type === "text") return <Type className={cls} />;
  return null;
}
const TYPE_LABEL = { image: "Imagem", video: "Vídeo", dual: "Dupla de vídeos/imagens", gallery: "Galeria", text: "Texto" };

function MediaField({ value, onChange, accept, label, placeholder, testid }) {
  const isVideo = /\.(mp4|webm|mov)/i.test(value || "") || (value || "").startsWith("/api/media/");
  const isYT = /youtu\.?be/i.test(value || "");
  return (
    <div>
      <label className="text-xs text-neutral-500 block mb-1">{label}</label>
      {value && (
        <div className="mb-2 aspect-video bg-black rounded overflow-hidden max-w-md">
          {isYT ? (
            <div className="w-full h-full flex items-center justify-center text-white/60 text-xs">YouTube: {value}</div>
          ) : isVideo ? (
            <video src={resolveSrc(value)} className="w-full h-full object-cover" muted />
          ) : (
            <img src={resolveSrc(value)} alt="" className="w-full h-full object-cover" />
          )}
        </div>
      )}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "URL YouTube, imagem ou vídeo"} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono text-xs mb-2" />
      <DropZone multiple={false} accept={accept} hint={accept?.["video/*"] ? "MP4/WebM/MOV · até 100MB" : "Imagem · até 25MB"} onUploaded={(f) => onChange(f.url)} />
    </div>
  );
}

function ImageBlockEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      <MediaField value={block.src} onChange={(v) => upd({ src: v })} label="Imagem" testid={`block-image-src-${index}`} />
      <div>
        <label className="text-xs text-neutral-500">Alt (acessibilidade + SEO)</label>
        <input value={block.alt || ""} onChange={(e) => upd({ alt: e.target.value })} placeholder="Ex.: Fachada do empreendimento ao entardecer" data-testid={`block-image-alt-${index}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

function VideoBlockEditor({ block, upd, index }) {
  return (
    <div className="space-y-3">
      <MediaField value={block.src} onChange={(v) => upd({ src: v })} accept={{ "video/*": [".mp4", ".webm", ".mov", ".m4v"] }} label="Vídeo (URL YouTube/Vimeo ou upload)" testid={`block-video-src-${index}`} />
      <MediaField value={block.poster} onChange={(v) => upd({ poster: v })} label="Poster (opcional — capa do vídeo)" testid={`block-video-poster-${index}`} />
      <div>
        <label className="text-xs text-neutral-500">Alt (descrição para SEO)</label>
        <input value={block.alt || ""} onChange={(e) => upd({ alt: e.target.value })} data-testid={`block-video-alt-${index}`} className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>
    </div>
  );
}

function DualBlockEditor({ block, upd, index }) {
  const setSide = (side, patch) => upd({ [side]: { ...(block[side] || {}), ...patch } });
  const Side = ({ label, side }) => (
    <div className="space-y-2 border border-black/5 rounded p-3">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <select value={(block[side] || {}).kind || "image"} onChange={(e) => setSide(side, { kind: e.target.value })} className="border border-black/15 px-2 py-1 rounded text-xs bg-white" data-testid={`block-dual-${side}-kind-${index}`}>
        <option value="image">Imagem</option>
        <option value="video">Vídeo</option>
      </select>
      <MediaField value={(block[side] || {}).src} onChange={(v) => setSide(side, { src: v })} label="Mídia (URL ou upload)" testid={`block-dual-${side}-src-${index}`} />
      <input value={(block[side] || {}).alt || ""} onChange={(e) => setSide(side, { alt: e.target.value })} placeholder="Alt" className="w-full border border-black/15 px-2 py-1.5 rounded text-xs" />
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Side label="Esquerda" side="left" />
      <Side label="Direita" side="right" />
    </div>
  );
}

function GalleryBlockEditor({ block, upd, index }) {
  const items = block.items || [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const setItem = (i, patch) => {
    const next = items.map((it, j) => j === i ? { ...it, ...patch } : it);
    upd({ items: next });
  };
  const remove = (i) => upd({ items: items.filter((_, j) => j !== i) });
  const addFromUpload = (f) => upd({ items: [...items, { _id: `g_${Date.now()}_${Math.random()}`, src: f.url, kind: /video/i.test(f.content_type) ? "video" : "image", alt: "" }] });
  const addUrl = () => upd({ items: [...items, { _id: `g_${Date.now()}_${Math.random()}`, src: "", kind: "image", alt: "" }] });
  // ensure each item has a stable id
  const withIds = items.map((it, i) => ({ ...it, _id: it._id || `g_static_${i}` }));
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = withIds.findIndex((it) => it._id === active.id);
    const newI = withIds.findIndex((it) => it._id === over.id);
    upd({ items: arrayMove(withIds, oldI, newI) });
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-neutral-500">
        Galeria estilo Behance. Arraste os cards para reordenar (drag-and-drop). A ordem em que aparecem aqui é exatamente a ordem no site (esquerda→direita, cima→baixo).
      </div>
      {/* Layout config: quantos itens por linha no SITE PÚBLICO. `auto` mantém
          o algoritmo justificado (linhas cheias sem gap), enquanto 2/3/4/5/6
          força uma grade fixa (mesma coluna em todas as linhas). */}
      <div className="flex flex-wrap items-center gap-3 border border-black/10 rounded p-2 bg-neutral-50">
        <label className="text-[11px] flex items-center gap-2">
          <span className="uppercase tracking-widest text-neutral-500">Colunas no site:</span>
          <select
            value={block.per_row || "auto"}
            onChange={(e) => upd({ per_row: e.target.value === "auto" ? undefined : Number(e.target.value) })}
            data-testid={`gallery-per-row-${index}`}
            className="text-xs border border-black/15 rounded px-2 py-1 bg-white"
          >
            <option value="auto">Automático (justificado)</option>
            <option value="2">2 por linha</option>
            <option value="3">3 por linha</option>
            <option value="4">4 por linha</option>
            <option value="5">5 por linha</option>
            <option value="6">6 por linha</option>
          </select>
        </label>
        <span className="text-[10px] text-neutral-400">
          "Automático" preenche cada linha por proporção; forçar N colunas mantém tudo alinhado como grade.
        </span>
      </div>
      {withIds.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={withIds.map((it) => it._id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`gallery-items-${index}`}>
              {withIds.map((it, i) => (
                <GalleryItemSortable key={it._id} id={it._id} it={it} i={i} setItem={setItem} remove={remove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex gap-2 items-center flex-wrap">
        <DropZone multiple onUploaded={(arr) => Array.isArray(arr) ? arr.forEach(addFromUpload) : addFromUpload(arr)} accept={{ "image/*": [], "video/*": [".mp4", ".webm", ".mov"] }} hint="Imagens/GIFs/vídeos — até 25MB (imagem) / 100MB (vídeo)" />
        <button onClick={addUrl} data-testid={`gallery-add-url-${index}`} className="text-xs bg-black text-white px-3 py-2 rounded-full hover:bg-accent-yellow hover:text-black transition-colors"><Plus className="w-3 h-3 inline" /> URL/YouTube</button>
      </div>
    </div>
  );
}

function GalleryItemSortable({ id, it, i, setItem, remove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative border border-black/10 rounded overflow-hidden bg-white" data-testid={`gallery-item-${i}`}>
      <button {...attributes} {...listeners} className="absolute top-1 left-1 z-10 cursor-grab active:cursor-grabbing bg-black/70 text-white rounded p-1 opacity-80 hover:opacity-100" title="Arrastar para reordenar" aria-label="Arrastar">
        <GripVertical className="w-3 h-3" />
      </button>
      <div className="aspect-square bg-neutral-100">
        {it.src && (/youtu\.?be/i.test(it.src) ? (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-400 p-2 text-center">YT: {it.src.slice(-24)}</div>
        ) : /\.(mp4|webm|mov)/i.test(it.src) || (it.src.startsWith("/api/media/") && it.kind === "video") ? (
          <video src={resolveSrc(it.src)} className="w-full h-full object-cover" muted />
        ) : (
          <img src={resolveSrc(it.src)} alt="" className="w-full h-full object-cover" />
        ))}
      </div>
      <button onClick={() => remove(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded p-1 opacity-90"><X className="w-3 h-3" /></button>
      <div className="p-1 space-y-1">
        <select value={it.kind || "image"} onChange={(e) => setItem(i, { kind: e.target.value })} className="w-full border border-black/10 rounded text-[10px] px-1 py-0.5 bg-white">
          <option value="image">Imagem</option>
          <option value="video">Vídeo</option>
          <option value="gif">GIF</option>
        </select>
        <input value={it.src || ""} onChange={(e) => setItem(i, { src: e.target.value })} placeholder="URL/YT" className="w-full border border-black/10 rounded text-[10px] px-1 py-0.5 font-mono" />
        <input value={it.alt || ""} onChange={(e) => setItem(i, { alt: e.target.value })} placeholder="alt" className="w-full border border-black/10 rounded text-[10px] px-1 py-0.5" />
      </div>
    </div>
  );
}

export default function ContentBlocksEditor({ blocks = [], onChange, testidPrefix = "content-blocks" }) {
  return <ContentBlocksEditorImpl blocks={blocks} onChange={onChange} testidPrefix={testidPrefix} />;
}

const POPULAR_FONTS = [
  "Playfair Display", "Cormorant Garamond", "Lora", "Merriweather", "Libre Baskerville",
  "Inter", "Poppins", "Montserrat", "Roboto", "Open Sans", "Work Sans", "DM Sans",
  "Fraunces", "EB Garamond", "Crimson Pro", "PT Serif", "Source Serif Pro",
  "Bebas Neue", "Oswald", "Anton", "Cabin", "Nunito", "Rubik", "Space Grotesk",
];
const loadedAdminFonts = new Set();
function loadAdminFont(family) {
  if (!family || loadedAdminFonts.has(family)) return;
  const id = `admin-gfont-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) { loadedAdminFonts.add(family); return; }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700&display=swap`;
  document.head.appendChild(link);
  loadedAdminFonts.add(family);
}

function TextBlockEditor({ block, upd }) {
  React.useEffect(() => { if (block.font_family) loadAdminFont(block.font_family); }, [block.font_family]);
  const preview = {
    fontFamily: block.font_family ? `'${block.font_family}', serif` : undefined,
    fontSize: block.font_size_px ? `${block.font_size_px}px` : undefined,
    fontWeight: block.font_weight || undefined,
    fontStyle: block.italic ? "italic" : undefined,
    textAlign: block.align || undefined,
    color: block.color || undefined,
    lineHeight: block.line_height || 1.5,
    letterSpacing: block.letter_spacing_em ? `${block.letter_spacing_em}em` : undefined,
    textTransform: block.uppercase ? "uppercase" : undefined,
    textDecoration: block.underline ? "underline" : undefined,
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <label className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Fonte (Google Fonts)</div>
          <div className="flex gap-1">
            <select value={block.font_family || ""} onChange={(e) => { loadAdminFont(e.target.value); upd({ font_family: e.target.value }); }} className="flex-1 border border-black/15 rounded px-2 py-1.5">
              {POPULAR_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input value={block.font_family || ""} onChange={(e) => upd({ font_family: e.target.value })} placeholder="Ou digite" onBlur={(e) => loadAdminFont(e.target.value)} className="w-28 border border-black/15 rounded px-2 py-1.5" />
          </div>
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Tamanho (px)</div>
          <input type="number" min={8} max={200} value={block.font_size_px || 20} onChange={(e) => upd({ font_size_px: Number(e.target.value) })} className="w-full border border-black/15 rounded px-2 py-1.5" />
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Peso</div>
          <select value={block.font_weight || "400"} onChange={(e) => upd({ font_weight: e.target.value })} className="w-full border border-black/15 rounded px-2 py-1.5">
            {["300","400","500","600","700","800","900"].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Cor</div>
          <input type="color" value={block.color || "#111111"} onChange={(e) => upd({ color: e.target.value })} className="w-full h-9 border border-black/15 rounded cursor-pointer" />
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Alinhamento</div>
          <select value={block.align || "left"} onChange={(e) => upd({ align: e.target.value })} className="w-full border border-black/15 rounded px-2 py-1.5">
            {["left","center","right","justify"].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Line-height</div>
          <input type="number" step="0.1" min={1} max={3} value={block.line_height || 1.5} onChange={(e) => upd({ line_height: Number(e.target.value) })} className="w-full border border-black/15 rounded px-2 py-1.5" />
        </label>
        <label>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Tracking (em)</div>
          <input type="number" step="0.005" min={-0.05} max={0.3} value={block.letter_spacing_em || 0} onChange={(e) => upd({ letter_spacing_em: Number(e.target.value) })} className="w-full border border-black/15 rounded px-2 py-1.5" />
        </label>
      </div>
      <div className="flex flex-wrap gap-4 text-xs">
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!block.italic} onChange={(e) => upd({ italic: e.target.checked })} /> Itálico</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!block.underline} onChange={(e) => upd({ underline: e.target.checked })} /> Sublinhado</label>
        <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!block.uppercase} onChange={(e) => upd({ uppercase: e.target.checked })} /> MAIÚSCULAS</label>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Texto (permite HTML: &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;br/&gt;)</div>
        <textarea value={block.html ?? block.text ?? ""} onChange={(e) => upd({ html: e.target.value, text: e.target.value })} rows={5} className="w-full border border-black/15 rounded px-3 py-2 text-sm font-mono" placeholder="Escreva seu texto..." />
      </div>
      <div className="border border-dashed border-black/15 rounded p-4 bg-white">
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">Prévia</div>
        <div style={preview} className="whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: block.html || (block.text || "").replace(/\n/g, "<br/>") }} />
      </div>
    </div>
  );
}

function ContentBlocksEditorImpl({ blocks = [], onChange, testidPrefix = "content-blocks" }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [defaultSpacing, setDefaultSpacing] = useState(24);

  const handleEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (i, next) => onChange(blocks.map((b, j) => j === i ? next : b));
  const removeBlock = (id) => onChange(blocks.filter((b) => b.id !== id));

  const add = (type) => {
    const base = { id: newBlockId(), type, spacing_px: defaultSpacing };
    if (type === "image") Object.assign(base, { src: "", alt: "" });
    if (type === "video") Object.assign(base, { src: "", poster: "", alt: "" });
    if (type === "dual") Object.assign(base, { left: { kind: "image", src: "", alt: "" }, right: { kind: "image", src: "", alt: "" } });
    if (type === "gallery") Object.assign(base, { items: [] });
    if (type === "text") Object.assign(base, {
      html: "", text: "Escreva seu texto aqui...",
      font_family: "Playfair Display", font_size_px: 20, font_weight: "400",
      italic: false, uppercase: false, underline: false,
      align: "left", color: "#111111", line_height: 1.5, letter_spacing_em: 0,
    });
    onChange([...blocks, base]);
  };

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => add("image")} data-testid={`${testidPrefix}-add-image`} className="text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors"><ImageIcon className="w-3 h-3 inline mr-1" /> Imagem</button>
          <button onClick={() => add("video")} data-testid={`${testidPrefix}-add-video`} className="text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors"><Film className="w-3 h-3 inline mr-1" /> Vídeo</button>
          <button onClick={() => add("dual")} data-testid={`${testidPrefix}-add-dual`} className="text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors"><Columns className="w-3 h-3 inline mr-1" /> Dupla</button>
          <button onClick={() => add("gallery")} data-testid={`${testidPrefix}-add-gallery`} className="text-xs bg-accent-yellow text-black px-3 py-2 rounded-full font-semibold hover:bg-black hover:text-white transition-colors"><LayoutGrid className="w-3 h-3 inline mr-1" /> Galeria</button>
          <button onClick={() => add("text")} data-testid={`${testidPrefix}-add-text`} className="text-xs bg-accent-orange text-white px-3 py-2 rounded-full font-semibold hover:bg-black transition-colors"><Type className="w-3 h-3 inline mr-1" /> Texto</button>
        </div>
        <label className="text-[10px] text-neutral-500 flex items-center gap-1">
          Padrão novo bloco:
          <input type="range" min={0} max={200} step={4} value={defaultSpacing} onChange={(e) => setDefaultSpacing(Number(e.target.value))} className="w-20" />
          <span className="font-mono w-10">{defaultSpacing}px</span>
        </label>
      </div>
      {blocks.length === 0 ? (
        <div className="border border-dashed border-black/15 rounded-md p-10 text-center text-sm text-neutral-500">
          Nenhum bloco ainda. Adicione uma imagem, vídeo, dupla ou galeria para começar.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {blocks.map((block, i) => (
                <SortableBlock key={block.id} block={block} index={i} onChange={(next) => updateBlock(i, next)} onRemove={removeBlock} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
